import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import {
  LayoutDashboard,
  Scissors,
  Image,
  Star,
  CalendarDays,
  MessageSquare,
  LogOut,
  Plus,
  Trash2,
  Pencil,
  X,
  Save,
  Upload,
  Loader2,
  Bell,
  CheckCircle,
  Sparkles,
  Megaphone,
  ExternalLink,
  Copy,
  Eye,
  EyeOff,
  Send,
  Phone,
  Users,
  Mail,
  CalendarPlus,
  AlertTriangle,
  RotateCcw,
} from "lucide-react";
import { API, SITE_URL, authHeaders, resolveUrl, ImageUploadField } from "./shared";

const ReviewsTab = () => {
  const [reviews, setReviews] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", rating: 5, text: "", service: "", date: "" });

  const fetchReviews = useCallback(async () => {
    const res = await axios.get(`${API}/reviews`);
    setReviews(res.data);
  }, []);

  useEffect(() => { fetchReviews(); }, [fetchReviews]);

  const handleSave = async () => {
    try {
      if (editing) {
        await axios.put(`${API}/admin/reviews/${editing}`, form, authHeaders());
        toast.success("Review updated");
      } else {
        await axios.post(`${API}/admin/reviews`, form, authHeaders());
        toast.success("Review added");
      }
      setEditing(null);
      setShowAdd(false);
      setForm({ name: "", rating: 5, text: "", service: "", date: "" });
      fetchReviews();
    } catch (err) {
      toast.error("Failed to save");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this review?")) return;
    try {
      await axios.delete(`${API}/admin/reviews/${id}`, authHeaders());
      toast.success("Review deleted");
      fetchReviews();
    } catch (err) {
      toast.error("Failed to delete");
    }
  };

  const startEdit = (rev) => {
    setEditing(rev.id);
    setForm({ name: rev.name, rating: rev.rating, text: rev.text, service: rev.service, date: rev.date });
    setShowAdd(true);
  };

  return (
    <div data-testid="admin-reviews-tab">
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-serif text-2xl text-brand-text">Reviews</h2>
        <button onClick={() => { setShowAdd(!showAdd); setEditing(null); setForm({ name: "", rating: 5, text: "", service: "", date: "" }); }} data-testid="add-review-btn" className="flex items-center gap-2 px-4 py-2 bg-brand-gold text-black font-sans text-sm font-semibold hover:bg-brand-gold-light transition-colors">
          {showAdd ? <X size={14} /> : <Plus size={14} />}
          {showAdd ? "Cancel" : "Add Review"}
        </button>
      </div>

      {showAdd && (
        <div className="border border-brand-border bg-brand-surface p-5 mb-6 space-y-3" data-testid="review-form">
          <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Reviewer name" data-testid="review-name-input" className="bg-brand-bg border-brand-border text-brand-text rounded-none" />
          <Input value={form.service} onChange={(e) => setForm({ ...form, service: e.target.value })} placeholder="Service received" data-testid="review-service-input" className="bg-brand-bg border-brand-border text-brand-text rounded-none" />
          <Select value={String(form.rating)} onValueChange={(val) => setForm({ ...form, rating: parseInt(val) })}>
            <SelectTrigger className="bg-brand-bg border-brand-border text-brand-text rounded-none" data-testid="review-rating-select">
              <SelectValue placeholder="Rating" />
            </SelectTrigger>
            <SelectContent>
              {[5, 4, 3, 2, 1].map((r) => (
                <SelectItem key={r} value={String(r)}>{r} Star{r > 1 ? "s" : ""}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Textarea value={form.text} onChange={(e) => setForm({ ...form, text: e.target.value })} placeholder="Review text" data-testid="review-text-input" className="bg-brand-bg border-brand-border text-brand-text rounded-none" />
          <button onClick={handleSave} data-testid="save-review-btn" className="flex items-center gap-2 px-6 py-2 bg-brand-gold text-black font-sans text-sm font-semibold hover:bg-brand-gold-light transition-colors">
            <Save size={14} /> {editing ? "Update" : "Add"} Review
          </button>
        </div>
      )}

      <div className="space-y-3">
        {reviews.map((rev) => (
          <div key={rev.id} className="flex items-start gap-4 p-4 border border-brand-border bg-brand-surface" data-testid={`admin-review-${rev.id}`}>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <p className="font-sans text-sm font-medium text-brand-text">{rev.name}</p>
                <span className="font-sans text-xs text-brand-gold">{"*".repeat(rev.rating)}</span>
              </div>
              <p className="font-sans text-xs text-brand-text-muted mb-1">{rev.service} &middot; {rev.date}</p>
              <p className="font-sans text-xs text-brand-text-muted line-clamp-2">{rev.text}</p>
            </div>
            <div className="flex gap-2 flex-shrink-0">
              <button onClick={() => startEdit(rev)} data-testid={`edit-review-${rev.id}`} className="p-2 border border-brand-border text-brand-text-muted hover:text-brand-gold hover:border-brand-gold transition-colors">
                <Pencil size={14} />
              </button>
              <button onClick={() => handleDelete(rev.id)} data-testid={`delete-review-${rev.id}`} className="p-2 border border-brand-border text-brand-text-muted hover:text-red-400 hover:border-red-400 transition-colors">
                <Trash2 size={14} />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ReviewsTab;
